import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Menu,
  Modal,
  NumberInput,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAdjustments, IconCalendarPlus, IconX } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';

const formatMinor = (minor: number, currency: string) => {
  if (minor === 0) return 'Free';
  return `${currency} ${(minor / 100).toLocaleString()}`;
};

function CentralSubscriptions() {
  const utils = trpc.useUtils();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const list = trpc.billing.subscriptions.list.useQuery({
    status: statusFilter ?? undefined,
    take: 100,
  });

  const [adjustOpened, { open: openAdjust, close: closeAdjust }] = useDisclosure(false);
  const [target, setTarget] = useState<{ id: number; vendorId: number } | null>(null);
  const [action, setAction] = useState<
    'extend' | 'cancel_immediate' | 'cancel_at_period_end' | 'reactivate'
  >('extend');
  const [extendDays, setExtendDays] = useState<number>(30);

  const adjust = trpc.billing.subscriptions.adminAdjust.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Subscription updated', color: 'green' });
      void utils.billing.subscriptions.list.invalidate();
      closeAdjust();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  // Explicit row shape — breaks tsc's deep recursion (TS2589) on the
  // Table + tRPC + Mantine intersection.
  interface SubRow {
    id: number;
    vendorId: number;
    gateway: string;
    status: string;
    currentPeriodEnd: string | Date;
    cancelAtPeriodEnd: boolean;
    plan: { name: string; priceMinor: number; currency: string; interval: string } | null;
  }
  const rows = (list.data ?? []) as readonly SubRow[];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Subscriptions</Title>
          <Select
            placeholder="Filter status"
            clearable
            value={statusFilter}
            onChange={setStatusFilter}
            data={[
              { value: 'trialing', label: 'Trialing' },
              { value: 'active', label: 'Active' },
              { value: 'past_due', label: 'Past due' },
              { value: 'canceled', label: 'Canceled' },
              { value: 'expired', label: 'Expired' },
            ]}
            w={180}
          />
        </Group>

        <Card p={0}>
          {list.isPending ? (
            <Skeleton height={300} m="md" />
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Vendor</Table.Th>
                  <Table.Th>Plan</Table.Th>
                  <Table.Th>Gateway</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Period end</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((s) => (
                  <Table.Tr key={s.id}>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        #{s.vendorId}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{s.plan?.name ?? '—'}</Text>
                      <Text size="xs" c="dimmed">
                        {formatMinor(s.plan?.priceMinor ?? 0, s.plan?.currency ?? 'INR')} /{' '}
                        {s.plan?.interval}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light">{s.gateway}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={
                          s.status === 'active' || s.status === 'trialing'
                            ? 'green'
                            : s.status === 'past_due'
                              ? 'yellow'
                              : 'gray'
                        }
                        variant="light"
                      >
                        {s.status}
                      </Badge>
                      {s.cancelAtPeriodEnd && (
                        <Badge size="xs" color="orange" variant="light" ml={4}>
                          cancel-pending
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{new Date(s.currentPeriodEnd).toLocaleDateString()}</Text>
                    </Table.Td>
                    <Table.Td align="right">
                      <Menu position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle" aria-label="Adjust">
                            <IconAdjustments size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconCalendarPlus size={14} />}
                            onClick={() => {
                              setTarget({ id: s.id, vendorId: s.vendorId });
                              setAction('extend');
                              setExtendDays(30);
                              openAdjust();
                            }}
                          >
                            Extend period
                          </Menu.Item>
                          <Menu.Item
                            color="orange"
                            onClick={() => {
                              setTarget({ id: s.id, vendorId: s.vendorId });
                              setAction('cancel_at_period_end');
                              openAdjust();
                            }}
                          >
                            Cancel at period end
                          </Menu.Item>
                          <Menu.Item
                            color="red"
                            leftSection={<IconX size={14} />}
                            onClick={() => {
                              setTarget({ id: s.id, vendorId: s.vendorId });
                              setAction('cancel_immediate');
                              openAdjust();
                            }}
                          >
                            Cancel immediately
                          </Menu.Item>
                          <Menu.Item
                            onClick={() => {
                              setTarget({ id: s.id, vendorId: s.vendorId });
                              setAction('reactivate');
                              openAdjust();
                            }}
                          >
                            Reactivate
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(list.data ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center" py="md">
                        No subscriptions match.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <Modal opened={adjustOpened} onClose={closeAdjust} title="Adjust subscription" centered>
        <Stack>
          <Text size="sm">
            Subscription <strong>#{target?.id}</strong> for vendor #{target?.vendorId}.
          </Text>
          <Text size="sm">
            Action: <strong>{action.replace(/_/g, ' ')}</strong>
          </Text>
          {action === 'extend' && (
            <NumberInput
              label="Extend by (days)"
              min={1}
              max={3650}
              value={extendDays}
              onChange={(v) => setExtendDays(Number(v) || 30)}
            />
          )}
          <Group justify="end">
            <Button variant="default" onClick={closeAdjust}>
              Cancel
            </Button>
            <Button
              loading={adjust.isPending}
              onClick={() => {
                if (!target) return;
                adjust.mutate({
                  subscriptionId: target.id,
                  action,
                  extendDays: action === 'extend' ? extendDays : undefined,
                });
              }}
            >
              Apply
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

export const Route = createFileRoute('/central/subscriptions')({ component: CentralSubscriptions });
